import React, { useEffect, useState } from 'react';
import { useAolStore } from '../../../context/AolContext';
import { Table } from '../../../styles/table.style';
import { ValidatedNoonReport } from '../../../types/ViewModel/ValidatedNoonReport';
import { getNumberValue } from '../../../utils/aolFieldsHelper';
import { SummarySectionSectionInputElementWrapper, SummarySection, SummaryWrapper } from './SummarySection.styles';
import { AolGroup } from '../../../types/ViewModel/AolItem';
import OdfjellInputComponent from '../../common/odfjell-input-component/OdfjellInputComponent';
import useAolItemsHelper from '../../../utils/aol/useAolItemsHelper';
import SpinnerLoaderComponent from '../../common/loader/SpinnerLoader';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';

type SummaryVM = {
    hfoConsumption: number;
    mgoConsumption: number;
    hfoPricePerMT: number;
    mgoPricePerMT: number;
    hfoCost: number;
    mgoCost: number;
    observedDistance: number;
    loggedDistance: number;
    totalConsumption: { [key in FuelType]: number };
    fuelPrices: { [key in FuelType]: number };
};

const initialSummaryVM: SummaryVM = {
    hfoConsumption: 0,
    mgoConsumption: 0,
    hfoPricePerMT: 0,
    mgoPricePerMT: 0,
    hfoCost: 0,
    mgoCost: 0,
    observedDistance: 0,
    loggedDistance: 0,
    totalConsumption: {
        [FuelType.HFO]: 0,
        [FuelType.LGO]: 0,
        [FuelType.LSF]: 0,
        [FuelType.BIO]: 0,
    },
    fuelPrices: {
        [FuelType.HFO]: 0,
        [FuelType.LGO]: 0,
        [FuelType.LSF]: 0,
        [FuelType.BIO]: 0,
    },
};

const AolSummaryComponent: React.FC = () => {
    const [summaryVM, setSummaryVM] = useState<SummaryVM>(initialSummaryVM);
    const { selectedAolGroup, selectedAolDataItem } = useAolStore();
    const { getVoyageFuelPrice } = useAolItemsHelper();
    const { isLoadingEndpoint } = useApiLoaderHelper();

    useEffect(() => {
        if (selectedAolGroup && selectedAolDataItem) {
            updateSummaryVm(selectedAolGroup);
            setSummaryVM({ ...summaryVM });
        }
    }, [selectedAolGroup, selectedAolDataItem]);

    const updateSummaryVm = (aolGroup: AolGroup) => {
        const consumptionV5 = [] as Consumption[];
        const dataList = aolGroup.aolDataItems.map((o) => {
            const voyageFuelPrice = getVoyageFuelPrice(o);

            const hfoConsumption = calculateConsumptionForNoonReport(o.noonReport, 'hfo');
            const mgoConsumption = calculateConsumptionForNoonReport(o.noonReport, 'mgo');

            const hfoPrice = voyageFuelPrice?.hfoPrice ?? 0;
            let hfoCost = hfoConsumption * hfoPrice;
            const mgoPrice = voyageFuelPrice?.mgoPrice ?? 0;
            let mgoCost = mgoConsumption * mgoPrice;

            if (o.aolVersion >= 5) {
                if (o.noonReport?.enginesSection?.consumption && o.noonReport?.enginesSection?.consumption.length > 0) {
                    consumptionV5.push(...o.noonReport.enginesSection.consumption);
                }
                if (o.noonReport?.powerpacksSection?.consumption && o.noonReport?.powerpacksSection?.consumption.length > 0) {
                    consumptionV5.push(...o.noonReport.powerpacksSection.consumption);
                }
                if (o.noonReport?.inertGasProductionSection?.consumption && o.noonReport?.inertGasProductionSection?.consumption.length > 0) {
                    consumptionV5.push(...o.noonReport.inertGasProductionSection.consumption);
                }

                hfoCost += consumptionV5.reduce((a, b) => a + (b.fuelType === FuelType.HFO ? b.consumptionMt * hfoPrice : 0), 0);
                mgoCost += consumptionV5.reduce((a, b) => a + (b.fuelType === FuelType.LGO ? b.consumptionMt * mgoPrice : 0), 0);
            }

            const observedDistance = o.noonReport?.bridgeSection?.observedDistance ?? 0;
            const loggedDistance = o.noonReport?.bridgeSection?.loggedDistance ?? 0;

            return {
                hfoConsumption,
                hfoCost,
                mgoConsumption,
                mgoCost,
                observedDistance,
                loggedDistance,
            };
        });

        summaryVM.hfoConsumption = dataList.reduce((a, b) => a + b.hfoConsumption, 0);
        summaryVM.mgoConsumption = dataList.reduce((a, b) => a + b.mgoConsumption, 0);
        summaryVM.hfoCost = dataList.reduce((a, b) => a + b.hfoCost, 0);
        summaryVM.mgoCost = dataList.reduce((a, b) => a + b.mgoCost, 0);
        summaryVM.hfoPricePerMT = summaryVM.hfoCost > 0 ? summaryVM.hfoCost / summaryVM.hfoConsumption : 0;
        summaryVM.mgoPricePerMT = summaryVM.mgoCost > 0 ? summaryVM.mgoCost / summaryVM.mgoConsumption : 0;
        summaryVM.observedDistance = dataList.reduce((a, b) => a + b.observedDistance, 0);
        summaryVM.loggedDistance = dataList.reduce((a, b) => a + b.loggedDistance, 0);

        if (consumptionV5.length > 0) {
            summaryVM.totalConsumption[FuelType.HFO] =
                consumptionV5.reduce((a, b) => a + (b.fuelType === FuelType.HFO ? b.consumptionMt : 0), 0) + summaryVM.hfoConsumption;
            summaryVM.totalConsumption[FuelType.LGO] =
                consumptionV5.reduce((a, b) => a + (b.fuelType === FuelType.LGO ? b.consumptionMt : 0), 0) + summaryVM.mgoConsumption;
            summaryVM.totalConsumption[FuelType.LSF] = consumptionV5.reduce((a, b) => a + (b.fuelType === FuelType.LSF ? b.consumptionMt : 0), 0);
            summaryVM.totalConsumption[FuelType.BIO] = consumptionV5.reduce((a, b) => a + (b.fuelType === FuelType.BIO ? b.consumptionMt : 0), 0);

            const voyageFuelPrices = getVoyageFuelPrice(aolGroup.aolDataItems[0]);
            summaryVM.fuelPrices[FuelType.HFO] = voyageFuelPrices.hfoOnlyPrice;
            summaryVM.fuelPrices[FuelType.LGO] = voyageFuelPrices.mgoPrice;
            summaryVM.fuelPrices[FuelType.LSF] = voyageFuelPrices.lsfOnlyPrice;
            summaryVM.fuelPrices[FuelType.BIO] = voyageFuelPrices.bioPrice;
        }
    };

    const calculateConsumptionForNoonReport = (noonReport: ValidatedNoonReport | undefined, type: 'hfo' | 'mgo'): number => {
        let consumption = 0;
        const enginesSection = noonReport?.enginesSection;
        const powerpacksSection = noonReport?.powerpacksSection;
        const inertGasProductionSection = noonReport?.inertGasProductionSection;

        if (enginesSection) {
            consumption += getNumberValue(
                type === 'hfo' ? enginesSection.mainEnginesData?.consumptionHfo : enginesSection.mainEnginesData?.consumptionMgo,
                undefined,
                true
            );
            consumption += getNumberValue(
                type === 'hfo' ? enginesSection.auxiliaryEnginesConsumptionHfo : enginesSection.auxiliaryEnginesConsumptionMgo,
                undefined,
                true
            );
            consumption += getNumberValue(
                type === 'hfo' ? enginesSection.boilersConsumptionHfo : enginesSection.boilersConsumptionMgo,
                undefined,
                true
            );

            if (type === 'mgo') {
                consumption += getNumberValue(enginesSection.incineratorConsumptionMgo, undefined, true);
            }
        }

        if (type === 'mgo') {
            if (powerpacksSection && powerpacksSection.dieselGasoilDrivenPowerpacks) {
                consumption += powerpacksSection.dieselGasoilDrivenPowerpacks.reduce(
                    (a, b) => a + getNumberValue(b.consumptionMgo, undefined, true),
                    0
                );
            }
            if (inertGasProductionSection) {
                consumption += getNumberValue(inertGasProductionSection.inertGasGeneratorConsumptionMgo, undefined, true);
            }
        }

        return consumption;
    };

    return (
        <>
            {isLoadingEndpoint('GetVoyageFuelPrices') && (
                <SpinnerLoaderComponent message={isLoadingEndpoint('GetVoyageFuelPrices').message} clearDefaultText={true} isFeatureSpinner={true} />
            )}
            <SummaryWrapper>
                <h3>Summary</h3>
                <SummarySection>
                    <Table>
                        <thead>
                            <tr>
                                <th />
                                <th className="right">Consumption</th>
                                <th className="right">Fuel cost</th>
                                <th className="right">Price</th>
                            </tr>
                        </thead>
                        {selectedAolGroup?.aolDataItems?.some((o) => o.aolVersion >= 5) ? (
                            <tbody>
                                {Object.keys(summaryVM.totalConsumption)
                                    .filter((key) => summaryVM.totalConsumption[key] > 0)
                                    .map((key) => (
                                        <tr key={key}>
                                            <td className="bold-col">{FuelType[key]}</td>
                                            <td className="right">{summaryVM.totalConsumption[key].toFixed(1)} MT</td>
                                            <td className="right">{(summaryVM.totalConsumption[key] * summaryVM.fuelPrices[key]).toFixed(0)} USD</td>
                                            <td className="right">{summaryVM.fuelPrices[key].toFixed(0)} USD/MT</td>
                                        </tr>
                                    ))}
                                <tr>
                                    <td className="bold-col total">Total</td>
                                    <td className="right total">
                                        {(
                                            summaryVM.hfoConsumption +
                                            summaryVM.mgoConsumption +
                                            Object.values(summaryVM?.totalConsumption).reduce((a, b) => a + b, 0)
                                        ).toFixed(1)}
                                        {' MT'}
                                    </td>
                                    <td className="right total">
                                        {Object.keys(summaryVM?.totalConsumption)
                                            .reduce((a, b) => a + parseInt((summaryVM.totalConsumption[b] * summaryVM.fuelPrices[b]).toFixed(0)), 0)
                                            .toFixed(0)}
                                        {' USD'}
                                    </td>
                                    <td />
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                <tr>
                                    <td className="bold-col">HFO</td>
                                    <td className="right">{summaryVM.hfoConsumption.toFixed(1)} MT</td>
                                    <td className="right">{summaryVM.hfoCost?.toFixed(0)} USD</td>
                                    <td className="right">{summaryVM.hfoPricePerMT?.toFixed(0)} USD/MT</td>
                                </tr>
                                <tr>
                                    <td className="bold-col">MGO</td>
                                    <td className="right">{summaryVM.mgoConsumption.toFixed(1)} MT</td>
                                    <td className="right">{summaryVM.mgoCost?.toFixed(0)} USD</td>
                                    <td className="right">{summaryVM.mgoPricePerMT?.toFixed(0)} USD/MT</td>
                                </tr>
                                <tr>
                                    <td className="bold-col total">Total</td>
                                    <td className="right total">{(summaryVM.hfoConsumption + summaryVM.mgoConsumption).toFixed(1)} MT</td>
                                    <td className="right total">{(summaryVM.hfoCost + summaryVM.mgoCost).toFixed(0)} USD</td>
                                    <td />
                                </tr>
                            </tbody>
                        )}
                    </Table>
                </SummarySection>
                <SummarySectionSectionInputElementWrapper>
                    <OdfjellInputComponent
                        type={'number'}
                        isReadOnly={true}
                        description={'Observed distance'}
                        value={summaryVM.observedDistance}
                        unit={'NM'}
                    />
                    <OdfjellInputComponent
                        type={'number'}
                        isReadOnly={true}
                        description={'Logged distance'}
                        value={summaryVM.loggedDistance}
                        unit={'NM'}
                    />
                </SummarySectionSectionInputElementWrapper>
            </SummaryWrapper>
        </>
    );
};

export default AolSummaryComponent;
